import React, {
  MouseEvent,
  useCallback,
  useState,
} from 'react';
import Button from 'pages/Dashboard/components/Button';
import ToolTip from 'core/components/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import CheckIcon from '@mui/icons-material/Check';
import map from 'lodash/map';
import SortAscendingIcon from 'core/icons/sortAscending';
import SortDescendingIcon from 'core/icons/sortDescending';
import {
  SortOrder,
} from 'dtos/sortOrder';
import head from 'lodash/head';

type DirectionOption = {
  label: string;
  value: SortOrder;
};

export type SortOption<TSortBy extends string> = {
  label: string;
  value: TSortBy;
  defaultSortOrder: SortOrder;
  directionOptions: DirectionOption[];
};

export type SortValue<TSortBy extends string> = {
  sortBy: TSortBy;
  sortOrder: SortOrder;
};

type Props<TSortBy extends string> = {
  value: SortValue<TSortBy>;
  options: SortOption<TSortBy>[];
  onChange: (value: SortValue<TSortBy>) => void;
};

export default function SortBy<TSortBy extends string>({
  value,
  options,
  onChange,
}: Props<TSortBy>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const selectedOption = options.find((option) => option.value === value.sortBy) ?? head(options);
  const directionOptions = selectedOption?.directionOptions ?? [];

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSortByClick = (option: SortOption<TSortBy>) => {
    onChange({
      sortBy: option.value,
      sortOrder: option.defaultSortOrder,
    });
    handleClose();
  };

  const handleDirectionClick = (sortOrder: SortOrder) => {
    onChange({
      sortBy: value.sortBy,
      sortOrder,
    });
    handleClose();
  };

  const Icon = value.sortOrder === SortOrder.Ascending
    ? SortAscendingIcon
    : SortDescendingIcon;

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        className="!h-8"
        onClick={handleClick}
      >
        <ToolTip title="Sort by">
          <span className="flex gap-2">
            <Icon className="!w-4 !h-4 text-gray-600" />
          </span>
        </ToolTip>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        autoFocus
        slotProps={{
          paper: {
            sx: { minWidth: 200 },
          },
          list: {
            autoFocusItem: open,
            sx: {
              py: 0,
              '& .MuiMenuItem-root': {
                py: 1,
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent !important',
                  '&.Mui-focusVisible': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                  },
                },
              },
            },
          },
        }}
      >
        <ListSubheader
          className="!px-3 !py-1.5 mb-0.5 !text-sm !text-gray-700 !font-medium !bg-gray-50 !leading-normal !h-8"
          disableSticky
        >
          Sort by
        </ListSubheader>
        {map(options, (option) => {
          const isSelected = value.sortBy === option.value;

          return (
            <MenuItem
              key={option.value}
              onClick={() => { handleSortByClick(option); }}
              selected={isSelected}
              className="!text-xs"
            >
              <span className="flex items-center w-full gap-2">
                <span className="w-4 flex items-center justify-center">
                  {isSelected && <CheckIcon className="!w-4 !h-4 text-gray-700" />}
                </span>
                <span className="text-xs font-medium">{option.label}</span>
              </span>
            </MenuItem>
          );
        })}
        <ListSubheader
          className="!px-3 !py-1.5 !text-sm !text-gray-700 !font-medium !bg-gray-50 !border-t !leading-normal !h-8"
          disableSticky
        >
          Sort direction
        </ListSubheader>
        {map(directionOptions, (option) => {
          const isSelected = value.sortOrder === option.value;

          return (
            <MenuItem
              key={option.value}
              onClick={() => {
                handleDirectionClick(option.value);
              }}
              selected={isSelected}
              className="!text-xs"
            >
              <span className="flex items-center w-full gap-2">
                <span className="w-4 flex items-center justify-center">
                  {isSelected && <CheckIcon className="!w-4 !h-4 text-gray-700" />}
                </span>
                <span className="text-xs font-medium">{option.label}</span>
              </span>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
